import React from "react";
import { Box, Portal, Text } from "@mantine/core";
import moment from "moment";
import { SalonReport } from "../../../../types/reports";
import { ReportV2Definition } from "../../../../types/reportsV2";
import { CanvasHeaderProps } from "../canvasTypes";
import printStyles from "../print/ReportPrintView.module.css";
import DesktopSummaryCanvas from "./DesktopSummaryCanvas";
import { useSummaryCanvas } from "./hooks/useSummaryCanvas";
import MobileSummaryCanvas from "./MobileSummaryCanvas";
import SummaryLedgers from "./SummaryLedgers";

export interface SummaryCanvasProps {
  salonId: string;
  report: SalonReport;
  definition: ReportV2Definition;
  header: CanvasHeaderProps;
  onReportSaved: (report: SalonReport) => void;
  /** Shell Duplicate (copies the view on screen). */
  onDuplicate: () => void;
  onDirtyChange: (dirty: boolean) => void;
  /** Phone layout (the app's mobile breakpoint). */
  mobile?: boolean;
}

/**
 * A summary (ledger) report, e.g. the Sales Summary or Earnings Summary.
 * Owns the one `useSummaryCanvas` model — draft vs saved view, the run and
 * print — and renders it with the desktop or phone layout, so crossing the
 * mobile breakpoint keeps an unsaved view and the current result.
 */
export default function SummaryCanvas({
  salonId,
  report,
  definition,
  header,
  onReportSaved,
  onDuplicate,
  onDirtyChange,
  mobile = false,
}: SummaryCanvasProps) {
  const canvas = useSummaryCanvas({
    salonId,
    report,
    definition,
    name: header.name,
    onReportSaved,
    onDirtyChange,
  });
  const View = mobile ? MobileSummaryCanvas : DesktopSummaryCanvas;
  const printing = canvas.printer.printing ? canvas.settled : null;
  return (
    <>
      <View
        canvas={canvas}
        definition={definition}
        header={header}
        onDuplicate={onDuplicate}
      />
      {printing && (
        <Portal>
          <Box
            className={printStyles.printOnly}
            data-testid="summary-print-view"
          >
            <Box className={printStyles.root}>
              <SummaryLedgers name={header.name} data={printing} plain />
              <Text className={printStyles.subtitle}>
                Printed {moment().format("MMM D, YYYY")}
              </Text>
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
}
